import React, { useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faArrowUpWideShort, faArrowDownWideShort, faArrowDownArrowUp, faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';

import Tooltip from 'components/tooltip';
import { type Analytics } from './email_analytics_card';
import { formatDateTime } from 'helpers/formatters/date_time_formats';
import Pagination from 'shared/pagination';
import { Empty } from '@buildoutinc/blueprint-react/ui/Empty';

import {
  COLUMN_CONFIG,
  PAGE_SIZE,
  type SortColumn,
  type SortOrder
} from './email_details_card_util';

export type EmailDetailsProps = {
  emailAnalytics: Analytics[]
}

export default function EmailDetails({
  emailAnalytics,
}: EmailDetailsProps) {
  const [page, setPage] = useState(1);
  const [sortColumn, setSortColumn] = useState<SortColumn>('sentAt');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  const sortedEmailAnalytics = useMemo(() => {
    return [...emailAnalytics].sort((a, b) => {
      let aValue, bValue;
      const columnType = COLUMN_CONFIG[sortColumn].type;

      switch (columnType) {
        case 'string':
          aValue = (a[sortColumn] as string || '').toLowerCase();
          bValue = (b[sortColumn] as string || '').toLowerCase();
          break;
        case 'date':
          aValue = a[sortColumn] ? new Date(a[sortColumn]).getTime() : 0;
          bValue = b[sortColumn] ? new Date(b[sortColumn]).getTime() : 0;
          break;
        case 'number':
          aValue = a[sortColumn] ?? 0;
          bValue = b[sortColumn] ?? 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [emailAnalytics, sortColumn, sortOrder]);

  const paginatedEmailAnalytics = useMemo(() => {
    const startIndex = (page - 1) * PAGE_SIZE;
    return sortedEmailAnalytics.slice(startIndex, startIndex + PAGE_SIZE);
  }, [sortedEmailAnalytics, page]);

  const totalPages = useMemo(() => Math.ceil(sortedEmailAnalytics.length / PAGE_SIZE), [sortedEmailAnalytics.length, PAGE_SIZE]);

  const handleSortChange = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortOrder('desc');
    }
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  }

  const getSortIcon = (column) => {
    if (sortColumn !== column) {
      return faArrowDownArrowUp;
    }
    return sortOrder === 'asc' ? faArrowUpWideShort : faArrowDownWideShort;
  };

  const renderHeader = () => {
    return (
      <thead className="ds-leads-table-header">
        <tr>
          {Object.keys(COLUMN_CONFIG).map((key) => {
            return (
              <th key={key} className="text-nowrap">
                <div
                  className="d-flex align-items-center"
                  onClick={() => handleSortChange(key)}
                  style={{ cursor: 'pointer' }}
                >
                  {COLUMN_CONFIG[key].label}
                  <FontAwesomeIcon
                    className="ml-2"
                    icon={getSortIcon(key)}
                  />
                  {COLUMN_CONFIG[key].tooltip &&
                    <Tooltip
                      content={COLUMN_CONFIG[key].tooltip}
                      dark
                      placement="bottom"
                    >
                      <FontAwesomeIcon className="ml-2" icon={faInfoCircle}/>
                    </Tooltip>
                  }
                </div>
              </th>
            );
          })}
        </tr>
      </thead>
    );
  };

  const renderBody = () => {
    return (
      <tbody>
        {paginatedEmailAnalytics.map(emailAnalyticsItem => (
          <tr key={emailAnalyticsItem.id}>
            <td>{emailAnalyticsItem.subject}</td>
            <td>{formatDateTime(emailAnalyticsItem.sentAt)}</td>
            <td>{emailAnalyticsItem.numDelivered}</td>
            <td>{emailAnalyticsItem.clickRate}%</td>
            <td>{emailAnalyticsItem.openRate}%</td>
          </tr>
        ))}
      </tbody>
    );
  };

  const renderPagination = () => {
    return totalPages > 1 ? (
      <div className="text-center">
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onChange={handlePageChange}
          showFirstLast={true}
          showLabels={true}
          showJumpTo={true}
        />
      </div>
    ) : null;
  };

  const renderDetailsTable = () => {
    return (
      <div className="table-container">
        <table className="table">
          {renderHeader()}
          {renderBody()}
        </table>
        {renderPagination()}
      </div>
    );
  };

  const renderEmptyState = () => {
    return (
      <Empty className="bg-white" style={{ minHeight: '250px' }}>
        <Empty.Media>
          <FontAwesomeIcon icon={faExclamationCircle} />
        </Empty.Media>
        <Empty.Title>No email analytics to display</Empty.Title>
        <Empty.Content>Waiting For Data...</Empty.Content>
      </Empty>
    );
  };

  return (
    <div className="ds-client-report-card d-flex flex-column gap-4 p-4">
      <div className="d-flex justify-content-between align-items-center">
        <h6 className="m-0 fw-normal">Email Details</h6>
      </div>
      {
        emailAnalytics.length === 0 ?
          renderEmptyState() :
          renderDetailsTable()
      }
    </div>
  );
}
